import React, { useState, useCallback, useRef, useEffect } from 'react'
import { string } from 'prop-types'
import { down } from 'styled-breakpoints'
import styled from 'styled-components'
import { gsap } from 'gsap'
import { ReactComponent as AddIcon } from '../../assets/svg/add-circle-line.svg'
import Typography from './Typography'
import { TABLET } from '../../lib/constant'

function Accordion({ summary, details }) {
	const [expanded, setExpanded] = useState(false)

	const handleChange = useCallback(() => {
		setExpanded((prevState) => !prevState)
	}, [setExpanded])

	const detailRef = useRef(null)

	useEffect(() => {
		if (!detailRef.current) {
			return
		}
		if (expanded) {
			gsap.set(detailRef.current, {
				height: 'auto',
				margin: '32px 0',
			})
			gsap.from(detailRef.current, {
				height: 0,
				duration: 0.2,
				margin: 0,
			})
			return
		}
		gsap.to(detailRef.current, {
			duration: 0.2,
			height: 0,
			margin: 0,
		})
	}, [expanded])

	return (
		<AccordionBlock>
			<AccordionSummary onClick={handleChange}>
				<Typography className="summary" fontWeight={500}>
					{summary}
				</Typography>
				<IconBlock expanded={expanded}>
					<AddIcon fill="black" />
				</IconBlock>
			</AccordionSummary>
			<AccordionDetails ref={detailRef}>
				<Typography>{details}</Typography>
			</AccordionDetails>
		</AccordionBlock>
	)
}

Accordion.propTypes = {
	summary: string,
	details: string,
}

Accordion.defaultProps = {
	summary: '',
	details: '',
}

export default React.memo(Accordion)

const AccordionBlock = styled.div`
	width: 100%;
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray[100]};
	overflow: hidden;
`

const AccordionSummary = styled.div`
	padding: 20px 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	cursor: pointer;
	user-select: none;
	font-weight: 500;
	font-size: 24px;
	line-height: 28px;

	${down(TABLET)} {
		font-size: 16px;
		line-height: 24px;
	}

	&:hover {
		& > .summary {
			color: ${({ theme }) => theme.colors.red[500]};
		}

		svg {
			fill: ${({ theme }) => theme.colors.gray[500]};
		}
	}
`

const AccordionDetails = styled.div`
	transition: all 0.1s ease-out;
	height: 0;
	overflow: hidden;
	white-space: pre-wrap;
	font-size: 18px;
	line-height: 28px;

	${down(TABLET)} {
		font-size: 14px;
		line-height: 20px;
	}
`

const IconBlock = styled.div`
	display: flex;
	transition: all 0.1s ease-out;
	margin-left: 16px;
	${({ expanded }) => expanded && 'transform: rotate(-45deg);'}
`
